// React and related imports
import React from 'react';

// Flowbite components
import {
  Alert,
  Badge,
  Button,
  Label,
  Modal,
  TextInput,
  ToggleSwitch,
} from 'flowbite-react';

// React Icons
import { HiInformationCircle } from 'react-icons/hi';

// i18 hooks
import { useTranslation } from 'react-i18next';

// RTK Query Hooks
import {
  useCreateContentMutation,
  useUpdateContentMutation,
} from '../../redux/api/contentApi';
import { useFetchMediaQuery } from '../../redux/api/mediaApi';
import { useFetchActiveQuestionsQuery } from '../../redux/api/flowApi';

// Redux selectors, hooks, and actions
import { useAppDispatch } from '../../redux/hooks/useAppDispatch';
import { useAppSelector } from '../../redux/hooks/useAppSelector';
import {
  clearAllValidationErrors,
  clearSelectedContent,
  clearValidationErrors,
  closeContentModal,
  setValidationErrors,
} from '../../redux/slices/contentSlice';
import {
  selectFormHasErrors,
  selectIsContentModalOpenedForAdd,
  selectSelectedContent,
  selectValidationErrors,
} from '../../redux/selectors/contentSelectors';
import { selectIsDarkMode } from '../../redux/selectors/displaySettingsSelectors';

// Types and Enums
import { IContent } from '../../common/interfaces/Content.interface';
import { Status } from '../../common/enums/Status';

// Custom Components
import { MultiLangContentInputs } from './MultiLangContentInputs';
import { AlertContentVisibility } from '../Shared/AlertContentVisibility';
import { MediaGalleryModal } from '../MediaGallery/MediaGalleryModal';
import { QuestionsDropdown } from './QuestionsDropdown';

// Custom Hooks
import { useContentState } from '../../hooks/useContentState';
import { useMediaGallery } from '../../hooks/useMediaGallery';
import { useFormChanges } from '../../hooks/useFormChanges';
import { usePreventUnload } from '../../hooks/usePreventUnload';

// Constants
import { statusIcons } from '../../common/constants/statusIcons';

// Utils
import { checkFieldValidity } from '../../utils/checkFieldValidity';
import { processContentState } from './utils/processContentState';

export const ContentModal: React.FC = () => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();

  const isDarkMode = useAppSelector(selectIsDarkMode);

  const isModalForAdd = useAppSelector(selectIsContentModalOpenedForAdd);
  const hasErrors = useAppSelector(selectFormHasErrors);
  const formErrors = Object.values(
    useAppSelector(selectValidationErrors)
  ).flat();
  const selectedContent = useAppSelector(selectSelectedContent);

  const [updateContent, { isLoading }] = useUpdateContentMutation();

  const [createContent, { isLoading: creating }] = useCreateContentMutation();

  const { data: mediaData } = useFetchMediaQuery({ page: 1, size: 50 });
  const { data: questionsData } = useFetchActiveQuestionsQuery();

  const questionsResponse = questionsData?.data || [];

  const { isModalOpen, contentState, updateField } = useContentState();

  const {
    showMediaGallery,
    selectedMedia,
    setShowMediaGallery,
    handleSelectMedia,
    handleSubmitSelectedMedia,
    handleCloseMediaModal,
  } = useMediaGallery(contentState, updateField);

  const isLoadingState = isLoading || creating;

  const { hasChanges, hasChangesRef, resetInitialValues } =
    useFormChanges<IContent>({
      initialValues: selectedContent as IContent,
      currentValues: contentState!,
    });

  usePreventUnload({
    shouldPrevent: hasChanges && isModalOpen,
  });

  const onCloseContentModal = () => {
    if (hasChangesRef.current) {
      const shouldClose = window.confirm(
        t('content.unsavedChangesCloseConfirm')
      );
      if (!shouldClose) return; // If user cancels, don't close the modal
    }

    dispatch(clearSelectedContent());
    dispatch(closeContentModal());
    dispatch(clearAllValidationErrors());
  };

  const validateField = (field: string, value = '') => {
    const errors = checkFieldValidity(field, value);
    if (errors.length > 0) {
      dispatch(setValidationErrors({ field, errors }));
    } else {
      dispatch(clearValidationErrors(field));
    }
  };

  const handleCreate = async () => {
    try {
      if (contentState) {
        const addedContent = processContentState(contentState);
        await createContent(addedContent).unwrap();
        resetInitialValues(onCloseContentModal);
      }
    } catch (err) {
      // The toast middleware reports the failure; the modal stays open with the input.
    }
  };

  const handleSaveEdits = async () => {
    try {
      if (contentState && contentState.id) {
        const editedContent = processContentState(contentState);
        await updateContent({
          id: contentState.id,
          updatedContent: editedContent,
        }).unwrap();
        resetInitialValues(onCloseContentModal);
      }
    } catch (err) {
      // The toast middleware reports the failure; the modal stays open with the input.
    }
  };

  const buttonClass = `btn ${isDarkMode ? 'btn-dark' : 'btn-light'}`;

  return (
    <Modal
      dismissible
      show={isModalOpen}
      size="5xl"
      onClose={onCloseContentModal}
      style={{ overflowY: 'hidden' }}
    >
      <Modal.Header className="modal-header">
        <div className="flex items-center">
          <span className="me-3">
            {isModalForAdd ? t('content.addContent') : t('content.editContent')}
          </span>
          <Badge
            size="sm"
            className="cursor-default bg-gray-200 text-gray-800 group-hover:bg-gray-200 dark:bg-gray-200 dark:text-gray-800 dark:group-hover:bg-gray-200"
          >
            {(contentState?.media?.type &&
              t(`mediaTypes.${contentState.media.type}`)) ||
              t(`contentTypes.${contentState?.contentType}`)}

            {/* Status Indicator */}
            <span
              className={`ms-2 inline-block h-2 w-2 rounded-full ${statusIcons[contentState?.status || Status.INACTIVE]}`}
            ></span>
          </Badge>
        </div>
      </Modal.Header>

      {/* Passing Criteria Alert  */}
      {formErrors && formErrors.length > 0 && (
        <Alert
          className={`m-auto mt-3 w-11/12`}
          withBorderAccent
          additionalContent={
            <AlertContentVisibility t={t} errors={formErrors} />
          }
          color="warning"
          icon={HiInformationCircle}
        >
          <span className="me-1 ms-1 font-extrabold">{t('alert.info')}</span>
          {isModalForAdd
            ? t('alert.contentAddMessage')
            : t('alert.contentEditMessage')}
        </Alert>
      )}

      <Modal.Body className="space-y-5">
        {/* Multi-language Content inputs */}
        <MultiLangContentInputs
          t={t}
          contentState={contentState}
          updateField={updateField}
          validateField={validateField}
        />

        {/* Status toggle */}
        <div className="ms-2">
          <div className="mb-2 block">
            <Label htmlFor="status" value={t('content.status')} />
          </div>
          <ToggleSwitch
            id="status"
            checked={contentState?.status === Status.ACTIVE}
            label={t(`status.${contentState?.status || Status.INACTIVE}`)}
            onChange={() =>
              updateField(
                'status',
                contentState?.status === Status.ACTIVE
                  ? Status.INACTIVE
                  : Status.ACTIVE
              )
            }
          />
        </div>

        {/* Media Gallery */}
        <div className="flex items-center space-x-2 pb-2">
          {/* Disabled Input Field for Media Name */}
          <TextInput
            type="text"
            value={selectedMedia?.name || contentState?.media?.name || ''}
            disabled
            placeholder={t('content.noMediaSelected')}
            className="w-3/5 p-2"
          />

          {/* Choose Media Button */}
          <Button
            type="button"
            className={`${buttonClass} flex-shrink-0`}
            onClick={() => setShowMediaGallery(true)}
          >
            {t('content.chooseMedia')}
          </Button>

          <MediaGalleryModal
            show={showMediaGallery}
            onClose={handleCloseMediaModal}
            onSelect={handleSelectMedia}
            onSubmit={handleSubmitSelectedMedia}
            selectedMedia={selectedMedia!}
            mediaItems={mediaData?.data || []}
          />
        </div>

        {/* Questions Selector */}
        <div className="ms-2">
          {contentState && (
            <QuestionsDropdown
              contentState={contentState}
              questionsResponse={questionsResponse}
              updateHandler={updateField}
              validateField={validateField}
            />
          )}
        </div>
      </Modal.Body>
      <Modal.Footer>
        <Button
          type="button"
          className={buttonClass}
          onClick={isModalForAdd ? handleCreate : handleSaveEdits}
          disabled={
            (!hasChanges && !isModalForAdd) || hasErrors || isLoadingState
          }
        >
          {t('content.save')}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};
